/* eslint-disable import/no-default-export */
import classnames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import { Dispatch } from 'app/gamma/src/types';
import _ from 'underscore';
import {
  BannerProps,
  TeamBoardsLinkBanner,
} from 'app/scripts/views/board-list/board-list-team-board-link-component';

import Collapsible from 'app/gamma/src/components/collapsible';
import { showLessActiveBoards } from 'app/gamma/src/modules/state/ui/boards-menu';
import {
  BoardStarUpdate,
  updateBoardStarPosition,
} from 'app/gamma/src/modules/state/models/board-stars';
import { MemberContext, MemberInfo } from 'app/gamma/src/modules/context';
import {
  BoardsMenuCategoryModel,
  BoardsMenuCategoryType,
} from 'app/gamma/src/types/models';
import CategoryContent from './category-content';
import { TableIcon } from '@trello/nachos/icons/table';
import { Lozenge } from 'app/src/components/Lozenge';
import styles from './boards-menu.less';

import { Analytics } from '@trello/atlassian-analytics';
import {
  canViewTeamTablePage,
  getViewsUpsellFlag,
} from 'app/src/components/ViewsGenerics';
import { StarIcon } from '@trello/nachos/icons/star';
import { ClockIcon } from '@trello/nachos/icons/clock';
import { BoardIcon } from '@trello/nachos/icons/board';
import { OrganizationIcon } from '@trello/nachos/icons/organization';
const icons = {
  [BoardsMenuCategoryType.Starred]: <StarIcon />,
  [BoardsMenuCategoryType.Recent]: <ClockIcon />,
  [BoardsMenuCategoryType.Personal]: <BoardIcon />,
  [BoardsMenuCategoryType.Team]: <OrganizationIcon />,
};

import { forTemplate } from 'app/common/lib/util/i18n';
import RouterLink from 'app/common/components/singlespa/router-link';
import { BoardsMenuButton } from './boards-menu-button';
import { BoardsMenuVisibilityState } from 'app/src/components/BoardsMenuVisibility';
const format = forTemplate('boards_sidebar_section');
const formatBoardsList = forTemplate('board_list');
const formatHome = forTemplate('home');

interface OwnProps {
  categories: BoardsMenuCategoryModel[];
  hidden: boolean;
  boardsMenuVisibility: BoardsMenuVisibilityState;
  setBoardsMenuVisibility: (s: BoardsMenuVisibilityState) => void;
}

interface DispatchProps {
  includeLessActiveBoards: (
    idTeam: string,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;

  reorderBoards: (data: BoardStarUpdate) => void;
}

interface AllProps extends OwnProps, DispatchProps {}

interface TeamBoardBannerWrapperProps extends Pick<BannerProps, 'children'> {
  category: BoardsMenuCategoryModel;
  isBoardsMenuPinned: boolean;
  closeBoardsMenu: () => void;
}

interface TeamTableLinkProps {
  url: string;
  category: string;
  idOrg: string;
  isFreeTeam: boolean;
  hidden: boolean;
}

const TeamBoardsBannerWrapper = ({
  category,
  isBoardsMenuPinned,
  closeBoardsMenu,
  children,
}: TeamBoardBannerWrapperProps) =>
  category.type === BoardsMenuCategoryType.Team && category.teamId ? (
    <TeamBoardsLinkBanner
      teamName={category.category}
      teamBoardsLink={`/${category.teamShortName}/home`}
      orgId={category.teamId}
      inBoardsMenu
      onBannerLinkClick={!isBoardsMenuPinned ? closeBoardsMenu : _.noop}
      emptyStateBanner={!category.boards.length}
    >
      {children}
    </TeamBoardsLinkBanner>
  ) : (
    <React.Fragment>
      {children({ shouldShowBanner: false, banner: null })}
    </React.Fragment>
  );

export const TeamTableLink: React.FunctionComponent<TeamTableLinkProps> = ({
  url,
  category,
  idOrg,
  isFreeTeam,
  hidden,
}) => {
  useEffect(() => {
    if (!hidden) {
      Analytics.sendViewedComponentEvent({
        componentName: 'teamTableLink',
        componentType: 'link',
        source: 'boardsMenuInlineDialog',
        containers: {
          organization: {
            id: idOrg,
          },
        },
        attributes: {
          isFreeTeam,
        },
      });
    }
  }, [idOrg, hidden, isFreeTeam]);

  const onClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'teamTableLink',
      source: 'boardsMenuInlineDialog',
      containers: {
        organization: {
          id: idOrg,
        },
      },
      attributes: {
        isFreeTeam,
      },
    });
  }, [idOrg, isFreeTeam]);

  return (
    <div className={styles.boardTile}>
      <RouterLink
        href={`${url}/tables`}
        className={styles.boardLink}
        onClick={onClick}
      >
        <div className={styles.boardThumbnail}>
          <div className={styles.tableIcon}>
            <TableIcon size="small" />
          </div>
        </div>
        <div className={styles.boardDescription}>
          <div className={styles.boardName}>{format('team-table-link')}</div>
        </div>
        <div className={styles.betaLozenge}>
          <Lozenge color="blue">{formatHome('beta')}</Lozenge>
        </div>
      </RouterLink>
    </div>
  );
};

const BoardList: React.FunctionComponent<AllProps> = ({
  categories,
  includeLessActiveBoards,
  reorderBoards,
  hidden,
  boardsMenuVisibility,
  setBoardsMenuVisibility,
}) => {
  useEffect(() => {
    //Sending analytics exposure events
    getViewsUpsellFlag('remarkable.org-table-view-free-team-upsell', false);
  }, []);

  return (
    <MemberContext.Consumer>
      {(memberInfo: MemberInfo) => {
        return (
          <div className={styles.boards} hidden={hidden}>
            {categories.map((categoryModel) => (
              <TeamBoardsBannerWrapper
                key={categoryModel.id}
                category={categoryModel}
                isBoardsMenuPinned={
                  boardsMenuVisibility === BoardsMenuVisibilityState.PINNED
                }
                closeBoardsMenu={() => {
                  setBoardsMenuVisibility(BoardsMenuVisibilityState.CLOSED);
                }}
              >
                {({
                  shouldShowBanner: shouldShowTeamBanner,
                  banner: teamBanner,
                }) => {
                  const {
                    boards,
                    category,
                    type,
                    id,
                    numLessActiveBoards = 0,
                    logos,
                    hasViewsFeature,
                    isPremiumTeam,
                    url,
                  } = categoryModel;

                  const isStarredType = type === BoardsMenuCategoryType.Starred;
                  const hasBoards = !!boards.length;

                  if (!isStarredType && !hasBoards && !shouldShowTeamBanner) {
                    return null;
                  }

                  const isTeam = type === BoardsMenuCategoryType.Team;
                  const headerClass = isTeam
                    ? classnames(styles.collapsibleHeader, styles.teamHeader)
                    : styles.collapsibleHeader;
                  const avatarURL = isTeam && !!logos ? logos['30'] : null;
                  const titleClass = classnames(
                    isTeam && styles.teamName,
                    avatarURL && styles.hasAvatar,
                  );
                  const idTeam = isTeam
                    ? id.replace(`${BoardsMenuCategoryType.Team}-`, '')
                    : id;

                  const onDragEnd = (result: DropResult) => {
                    const { draggableId, source, destination } = result;
                    if (
                      // they didn't drop it on a droppable
                      !destination ||
                      // they dropped it in the same position
                      (destination.droppableId === source.droppableId &&
                        destination.index === source.index)
                    ) {
                      return;
                    }

                    if (memberInfo.id) {
                      reorderBoards({
                        idBoard: draggableId,
                        idMember: memberInfo.id,
                        position: destination.index,
                      });
                    }
                  };

                  return (
                    <DragDropContext key={id} onDragEnd={onDragEnd}>
                      <Collapsible
                        key={`category-${id}`}
                        avatarURL={avatarURL}
                        icon={icons[type]}
                        id={idTeam}
                        url={url}
                        categoryId={id}
                        showPremiumIndicator={isPremiumTeam}
                        title={category}
                        open={true}
                        headerClass={headerClass}
                        tabIndex={0}
                        titleClass={titleClass}
                        toggleClass={styles.collapsibleToggle}
                        trackURLClick={() => {
                          Analytics.sendClickedLinkEvent({
                            linkName: 'orgPageLink',
                            source: 'boardsMenuInlineDialog',
                          });
                        }}
                        trackToggleClick={(closing: boolean) => {
                          Analytics.sendClickedButtonEvent({
                            buttonName: 'toggleBoardsListButton',
                            source: 'boardsMenuInlineDialog',
                            attributes: {
                              action: closing ? 'closed' : 'opened',
                            },
                            containers: {
                              organization: {
                                id: idTeam,
                              },
                            },
                          });
                        }}
                      >
                        {isStarredType && !hasBoards && (
                          <div className={styles.starredBoardsEmptyState}>
                            {format('star-your-most-important-boards')}
                          </div>
                        )}
                        {shouldShowTeamBanner && (
                          <div className={styles.joinTeamBoardsBanner}>
                            {teamBanner}
                          </div>
                        )}

                        {canViewTeamTablePage(hasViewsFeature, isPremiumTeam) &&
                          url && (
                            <TeamTableLink
                              url={url}
                              category={category}
                              idOrg={idTeam}
                              isFreeTeam={!!isPremiumTeam}
                              hidden={
                                boardsMenuVisibility ===
                                BoardsMenuVisibilityState.CLOSED
                              }
                            />
                          )}

                        <CategoryContent {...categoryModel} />
                        {numLessActiveBoards > 0 && (
                          <div>
                            <BoardsMenuButton
                              onClick={(
                                e: React.MouseEvent<HTMLButtonElement>,
                              ) => {
                                Analytics.sendClickedLinkEvent({
                                  linkName: 'showLessActiveBoardsLink',
                                  source: 'boardsMenuInlineDialog',
                                });

                                includeLessActiveBoards(idTeam, e);
                              }}
                              className={styles.showLessActive}
                            >
                              {formatBoardsList(
                                'show-less-active-boards-count',
                                {
                                  lessActiveCount: numLessActiveBoards.toString(),
                                },
                              )}
                            </BoardsMenuButton>
                          </div>
                        )}
                      </Collapsible>
                    </DragDropContext>
                  );
                }}
              </TeamBoardsBannerWrapper>
            ))}
          </div>
        );
      }}
    </MemberContext.Consumer>
  );
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    includeLessActiveBoards(idTeam, e) {
      // The boards menu container registers document-level click handlers, so
      // we need to mute the native event too to make sure this doesn't bubble
      // there.
      e.nativeEvent.stopImmediatePropagation();
      e.preventDefault();
      dispatch(showLessActiveBoards(idTeam));
    },

    reorderBoards(data) {
      dispatch(updateBoardStarPosition(data));
    },
  };
};

export default connect(null, mapDispatchToProps)(BoardList);
